/**
 * AdminChurchRoles — the movement's custom church roles.
 *
 * A super admin names a role (Elder, Deacon, Worship Leader…) and ticks the
 * permissions it carries. The role is then mirrored into every church's
 * catalogue, so a church admin can assign it to a member exactly like a
 * standard role — but cannot rename or delete it. The standard roles stay
 * locked and are listed here for reference.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ShieldCheck, Plus, Lock, Trash2, Edit3, Loader2, ArrowLeft, Church, Users,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = '/api/church/member-rbac/role-templates';

const api = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${r.status})`);
  }
  return r.json();
};

export default function AdminChurchRoles() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['church-role-templates'],
    queryFn: () => api(BASE),
    retry: false,
  });
  const roles = data?.roles || [];
  const catalogue = data?.permission_catalogue || [];
  const standard = data?.built_in_roles || [];

  const refresh = () => qc.invalidateQueries({ queryKey: ['church-role-templates'] });

  const del = useMutation({
    mutationFn: (id) => api(`${BASE}/${id}`, { method: 'DELETE' }),
    onSuccess: (res) => {
      toast.success(res?.assignments_removed
        ? `Role withdrawn — ${res.assignments_removed} assignment(s) removed`
        : 'Role withdrawn from every church');
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="church-roles-forbidden">
        <Lock className="h-8 w-8 mx-auto text-amber-500 mb-3" />
        <p className="text-sm text-slate-600">Only a Super Admin can build custom church roles.</p>
        <p className="text-xs text-slate-400 mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="admin-church-roles">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin" className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mt-1">
            <ShieldCheck className="h-5 w-5 text-emerald-600" /> Roles &amp; Permissions
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Build a role once — every church can then give it to a member alongside the standard roles.
          </p>
        </div>
        <Button onClick={() => setEditing({ name: '', description: '', permissions: ['view'] })}
                data-testid="church-role-new">
          <Plus className="h-4 w-4 mr-1" /> New custom role
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Custom roles ({roles.length})</CardTitle>
          <CardDescription>
            Available in every church. Churches can assign them but cannot rename or delete them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : roles.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400" data-testid="church-roles-empty">
              No custom roles yet. Add one — Worship Leader, Youth Pastor, Prayer Coordinator…
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {roles.map((r) => (
                <div key={r.id} className="rounded-lg border border-slate-200 p-3 bg-white"
                     data-testid={`church-role-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{r.name}</p>
                      {r.description && <p className="text-xs text-slate-500 mt-0.5">{r.description}</p>}
                      <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Church className="h-3 w-3" />{r.churches} church{r.churches === 1 ? '' : 'es'}</span>
                        <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{r.assigned} assigned</span>
                      </p>
                    </div>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => setEditing(r)} data-testid={`church-role-edit-${r.id}`}>
                        <Edit3 className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600"
                              onClick={() => {
                                if (window.confirm(`Withdraw "${r.name}" from every church? ${r.assigned} assignment(s) will be removed.`)) del.mutate(r.id);
                              }}
                              data-testid={`church-role-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(r.permissions || []).length === 0
                      ? <span className="text-[10px] italic text-slate-400">no permissions</span>
                      : r.permissions.map((p) => (
                        <span key={p} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                          {catalogue.find((c) => c.key === p)?.label || p}
                        </span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4 text-slate-400" /> Standard roles ({standard.length})
          </CardTitle>
          <CardDescription>
            Locked. Every church gets these; a church admin can re-permission its own copy.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2" data-testid="church-roles-standard">
            {standard.map((r) => (
              <Badge key={r.name} variant="outline" className="text-xs text-slate-600"
                     data-testid={`church-role-standard-${r.name.toLowerCase().replace(/ /g, '-')}`}>
                <Lock className="h-2.5 w-2.5 mr-1" />{r.name}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {editing && (
        <RoleDialog role={editing} catalogue={catalogue}
                    onClose={() => setEditing(null)}
                    onSaved={() => { refresh(); setEditing(null); }} />
      )}
    </div>
  );
}

function RoleDialog({ role, catalogue, onClose, onSaved }) {
  const isNew = !role.id;
  const [form, setForm] = useState({
    name: role.name || '',
    description: role.description || '',
    permissions: role.permissions || [],
  });
  const toggle = (key) => setForm((f) => ({
    ...f,
    permissions: f.permissions.includes(key)
      ? f.permissions.filter((p) => p !== key)
      : [...f.permissions, key],
  }));

  const save = useMutation({
    mutationFn: () => api(isNew ? BASE : `${BASE}/${role.id}`, {
      method: isNew ? 'POST' : 'PUT', body: JSON.stringify(form),
    }),
    onSuccess: () => { toast.success(isNew ? 'Custom role created' : 'Custom role updated'); onSaved(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="church-role-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New custom role' : `Edit · ${role.name}`}</DialogTitle>
          <DialogDescription>
            {isNew
              ? 'It appears in every church straight away, ready to assign to members.'
              : 'Changes reach every church that already carries this role.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Role name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Worship Leader" data-testid="church-role-name" />
          </div>
          <div>
            <Label>What this role does <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
            <Textarea rows={2} value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Leads the worship team and the Sunday song list."
                      data-testid="church-role-description" />
          </div>
          <div className="space-y-2">
            <Label>Permissions</Label>
            {catalogue.map((p) => (
              <label key={p.key} className="flex items-start gap-2 rounded-md border border-slate-200 p-2 cursor-pointer hover:bg-slate-50">
                <Checkbox checked={form.permissions.includes(p.key)}
                          onCheckedChange={() => toggle(p.key)}
                          data-testid={`church-role-perm-${p.key}`} />
                <span className="min-w-0">
                  <span className="text-sm text-slate-800 block">{p.label}</span>
                  <span className="text-xs text-slate-500">{p.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={save.isPending}
                  onClick={() => {
                    if (!form.name.trim()) return toast.error('Give the role a name');
                    save.mutate();
                  }}
                  data-testid="church-role-save">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {isNew ? 'Create role' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
